import { Splitter } from "@progress/kendo-react-layout";
import * as React from "react";
import { useLocation } from "react-router-dom";
import type { AILandingChatRef } from "@/components/AILanding/AILandingChat";
import { SubmitContext } from "@/components/AILanding/chatConnection/types";
import {
  useAccountSelection,
  useAccountsPayload,
} from "@/components/AILanding/hooks/useAccountSelection";
import { ConnectionStatus } from "@/types/aiLanding";

// Lazy-loaded: pulls in the chat/markdown/MCP-client dependency tree, which
// should only ship to users who actually open the TrustAI Copilot panel.
const AIInputContainer = React.lazy(
  () => import("@/components/AILanding/AIInputContainer"),
);
const AILandingChat = React.lazy(
  () => import("@/components/AILanding/AILandingChat"),
);

type TrustAISplitterPaneState = {
  size?: string;
  min?: string;
  max?: string;
  resizable?: boolean;
  collapsible?: boolean;
  collapsed?: boolean;
  keepMounted?: boolean;
  scrollable?: boolean;
};

const getTrustAISubmitContext = (pathname: string, search: string) => {
  const params = new URLSearchParams(search);
  const accountId = params.get("accountId");
  return {
    pathname,
    search,
    accountId,
  };
};

export const isTrustAISplitterEligible = (
  pathname: string,
  enabled: boolean,
  excludedPathname: string,
) => enabled && pathname.startsWith("/app/") && pathname !== excludedPathname;

interface TrustAISplitterLayoutProps {
  enabled: boolean;
  excludedPathname: string;
  title?: string;
  left: React.ReactNode;
  rightSize?: string;
  leftMinWidth?: string;
  panes?: TrustAISplitterPaneState[];
  onPanesChange?: (panes: TrustAISplitterPaneState[]) => void;
}

const TrustAISplitterLayout: React.FC<TrustAISplitterLayoutProps> = ({
  enabled,
  excludedPathname,
  title = "TrustAI Copilot",
  left,
  rightSize = "420px",
  leftMinWidth = "900px",
  panes,
  onPanesChange,
}) => {
  const location = useLocation();
  const eligible = React.useMemo(
    () =>
      isTrustAISplitterEligible(location.pathname, enabled, excludedPathname),
    [enabled, excludedPathname, location.pathname],
  );

  const submitContext = React.useMemo(
    () => getTrustAISubmitContext(location.pathname, location.search),
    [location.pathname, location.search],
  );

  const chatResetKey = React.useMemo(
    () => `${location.pathname}${location.search}`,
    [location.pathname, location.search],
  );

  if (!eligible) return <>{left}</>;
  const effectivePanes: TrustAISplitterPaneState[] = panes ?? [
    { resizable: true, scrollable: true, min: leftMinWidth },
    {
      resizable: true,
      scrollable: false,
      size: rightSize,
      min: "320px",
      collapsible: true,
      collapsed: true,
      keepMounted: true,
    },
  ];

  return (
    <Splitter
      orientation="horizontal"
      className="tlx-v3-trustai-splitter"
      style={{ height: "100%" }}
      panes={effectivePanes}
      onChange={
        onPanesChange
          ? event => onPanesChange(event.newState as TrustAISplitterPaneState[])
          : undefined
      }
    >
      <div className="tlx-v3-trustai-left">{left}</div>
      <div className="tlx-v3-trustai-right">
        <div className="tlx-v3-trustai-right__header">{title}</div>
        <div className="tlx-v3-trustai-right__content">
          <div className="tlx-v3-trustai-chat" key={chatResetKey}>
            <TrustAIChatContent submitContext={submitContext} />
          </div>
        </div>
      </div>
    </Splitter>
  );
};

export default TrustAISplitterLayout;

const TrustAIChatContent: React.FC<{
  submitContext: Record<string, unknown>;
}> = ({ submitContext }) => {
  const [connectionStatus, setConnectionStatus] =
    React.useState<ConnectionStatus>("connecting");
  const [isSending, setIsSending] = React.useState(false);
  const abortRequestRef = React.useRef<(() => void) | null>(null);
  const chatRef = React.useRef<AILandingChatRef | null>(null);
  const { selectedAccounts } = useAccountSelection();
  const handleAbortChange = React.useCallback((abortFn: () => void) => {
    abortRequestRef.current = abortFn;
  }, []);

  const handleAbort = React.useCallback(() => {
    abortRequestRef.current?.();
  }, []);

  const accountsPayload = useAccountsPayload(selectedAccounts);

  const handleSendMessage = React.useCallback(
    (message: string, context: SubmitContext) => {
      const mergedContext = {
        ...submitContext,
        ...context,
        accounts: accountsPayload,
      };
      chatRef.current?.submitQuery(message, mergedContext);
    },
    [submitContext, accountsPayload],
  );

  return (
    <React.Suspense fallback={null}>
      <div className="tlx-v3-trustai-chat__messages">
        <AILandingChat
          selectedAccounts={selectedAccounts}
          ref={chatRef}
          hideInput={true}
          onStatusChange={setConnectionStatus}
          onSendingChange={setIsSending}
          onAbortChange={handleAbortChange}
        />
      </div>
      <div className="tlx-v3-trustai-chat__input">
        <div className="ai-input-section has-messages">
          <div className="input-section-content">
            <AIInputContainer
              onSendMessage={handleSendMessage}
              maxLength={1000}
              connectionStatus={connectionStatus}
              isSending={isSending}
              onAbort={handleAbort}
              hideAccountSelection={true}
              selectedAccounts={selectedAccounts}
            />
          </div>
        </div>
      </div>
    </React.Suspense>
  );
};
